import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BetTableA2 from "../../casinoComponents/betTableA2";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import ViewMoreModal from "../../../components/ViewMoreModal";
import Lucky7CardsOverlay from "../../casinoComponents/videoBox/overlays/Lucky7CardsOverlay";
import Lucky7RefResults from "../resultui/resultLucky7/Lucky7RefResults";
import VAAAView from "./VAAAView";
import { getCasinoGameDetails, getMatchExposure, getMyBets } from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import "../../../styles/games/lucky7aaa.css";

// Reference game-header titles per API type (reference: lastresults2 res1.cname)
const TITLES = {
  aaa: "Amar Akbar Anthony",
  aaa2: "Amar Akbar Anthony 2",
  vaaa: "V-Amar Akbar Anthony",
};

/**
 * Amar Akbar Anthony — casino/aaa and casino/aaa2 in the reference shell (.casino-table.aaa),
 * vcasino/aaa2 (API type vaaa) in the vcasino layout.
 */
export default function AAA2() {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "aaa2";
  const isVirtual = pathname.includes("/vcasino/");

  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    fetchGameData();
    const gameDataInterval = setInterval(fetchGameData, 1500);
    return () => clearInterval(gameDataInterval);
  }, [gameName]);

  const matchId = gameData?.data?.data?.mid?.toString() || gameData?.data?.mid?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            exposureMap[item.team_name] = parseFloat(item.exposure_amount) || 0;
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            selection: bet.selection || bet.player_name || "",
            odds: bet.odds || "0",
            stake: bet.stake || "0",
            type: bet.type || null,
          }));
          setMyBets(formattedBets);
        }
      }
    } catch (error) {
      console.error("Error fetching my bets:", error);
    }
  }, [matchId]);

  useEffect(() => {
    if (!matchId) return;
    const refresh = () => {
      fetchExposure();
      fetchMyBetsData();
    };
    const timeoutId = setTimeout(refresh, 2000);
    const intervalId = setInterval(refresh, 2000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [matchId, fetchExposure, fetchMyBetsData]);

  // Admin panel places no bets (the old PlaceBet was a no-op stub): an odds click refreshes book + bets
  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const feed = gameData?.data?.data ?? gameData?.data ?? {};
  const sub = Array.isArray(feed?.sub) ? feed.sub : [];
  const roundId = feed?.mid || gameData?.mid;
  const title = TITLES[gameName] || TITLES.aaa2;
  const resultData = gameData?.lrs ?? feed?.lrs ?? [];
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const viewMoreModal = (
    <ViewMoreModal
      show={showViewMoreModal}
      onHide={() => setShowViewMoreModal(false)}
      title="View More"
      sectionKey={gameName}
    />
  );

  if (isVirtual) {
    return (
      <VAAAView
        gameName={gameName}
        title={title}
        data={feed}
        exposures={exposures}
        onBetClick={handleBetClick}
        myBets={myBets}
        onViewMore={() => setShowViewMoreModal(true)}
        viewMoreModal={viewMoreModal}
      />
    );
  }

  return (
    <CasinoTableLayout
      tableClass="aaa"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={roundId || ""} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={
            feed?.card != null ? <Lucky7CardsOverlay card={feed.card} dealerName={gameName !== "aaa2"} /> : null
          }
        />{" "}
        {/* Reference renders .casino-detail once the feed has a round (casinoData.mid) */}
        {roundId ? (
          <div className="casino-detail">
            <BetTableA2 sub={sub} onBetClick={handleBetClick} exposures={exposures} />{" "}
            {feed?.remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{feed.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              data={resultData}
              gameData={gameData}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={<Lucky7RefResults gameType={gameName} data={resultData} variant="aaa" fallbackTitle={title} />}
            />
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
